import React, { useCallback } from 'react';
import {
    IToastContextProps,
    ProviderClaim,
    ProviderPatientStatement,
} from '@enter/core';
import { RecordPaymentDialog } from './record-dialogs/record-payment-dialog';
import { RecordDiscountDialog } from './record-dialogs/record-discount-dialog';
import { RecordRefundDialog } from './record-dialogs/record-refund-dialog';
import { EditPaymentDialog } from './record-dialogs/edit-payment-dialog';
import {
    LEDGER_DIALOG_PAPER_PROPS,
    LEDGER_DIALOG_SX,
} from './record-dialogs/statement-ledger-dialog.styles';
import { buildStatementPrintViewModel } from './print/statement-print.utils';
import type { StatementPrintViewModel } from './print/statement-print.types';
import type { BalanceBreakdown } from './balance-breakdown';
import type { StatementCodeKeyItem } from './components/statement-code-key';
import type {
    IClaimStore,
    IDialogContext,
    IPatientStore,
    IStatementData,
} from './statement-details.types';

export type UseStatementActionsArgs = {
    patientId: string;
    patientStatement: ProviderPatientStatement | undefined;
    setPatientStatement: (statement: ProviderPatientStatement | undefined) => void;
    setLoading: (loading: boolean) => void;
    setPrintPreviewModel: (model: StatementPrintViewModel | null) => void;
    patientStore: IPatientStore;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
    equalsFilter: (term?: string | string[]) => string[] | undefined;
    filteredStatementData: IStatementData[];
    filteredBalance: string;
    balanceBreakdown: BalanceBreakdown | undefined;
    statementCodeKeyItems: StatementCodeKeyItem[];
    providerClaimsById: Map<string, ProviderClaim>;
};

export const useStatementActions = (args: UseStatementActionsArgs) => {
    const {
        patientId,
        patientStatement,
        setPatientStatement,
        setLoading,
        setPrintPreviewModel,
        patientStore,
        claimStore,
        dialogContext,
        toastContext,
        equalsFilter,
        filteredStatementData,
        filteredBalance,
        balanceBreakdown,
        statementCodeKeyItems,
        providerClaimsById,
    } = args;

    const reloadStatement = useCallback(async (): Promise<void> => {
        setLoading(true);
        try {
            const statement = await patientStore.loadPatientStatement(patientId);
            setPatientStatement(statement);
        } catch (err) {
            dialogContext.showError(err);
        } finally {
            setLoading(false);
        }
    }, [patientStore, patientId, dialogContext, setLoading, setPatientStatement]);

    const openPaymentActionDialog = useCallback(
        async (statementRow: IStatementData, mode: 'edit' | 'allocate'): Promise<void> => {
            let statementToUse = patientStatement;
            if (!statementToUse) {
                try {
                    setLoading(true);
                    statementToUse = await patientStore.loadPatientStatement(patientId);
                    setPatientStatement(statementToUse);
                } catch (err) {
                    dialogContext.showError(err);
                    return;
                } finally {
                    setLoading(false);
                }
            }

            const handleClose = () => {
                dialogContext.hideDialog();
            };

            const handleSuccess = async () => {
                handleClose();
                await reloadStatement();
            };

            dialogContext.showDialog({
                props: {
                    onClose: handleClose,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: mode === 'allocate' ? 'Allocate Payment to Claim' : 'Edit Payment',
                },
                Component: (
                    <EditPaymentDialog
                        patientStatement={statementToUse}
                        selectedRow={statementRow}
                        mode={mode}
                        onClose={handleClose}
                        onSuccess={handleSuccess}
                        claimStore={claimStore}
                        dialogContext={dialogContext}
                        toastContext={toastContext}
                    />
                ),
            });
        },
        [
            patientStatement,
            patientStore,
            patientId,
            dialogContext,
            claimStore,
            toastContext,
            reloadStatement,
            setLoading,
            setPatientStatement,
        ],
    );

    const handleEditPayment = useCallback(
        (statementRow: IStatementData) => openPaymentActionDialog(statementRow, 'edit'),
        [openPaymentActionDialog],
    );

    const handleAllocatePayment = useCallback(
        (statementRow: IStatementData) => openPaymentActionDialog(statementRow, 'allocate'),
        [openPaymentActionDialog],
    );

    const handleRecordPayment = useCallback((): void => {
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            await reloadStatement();
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                PaperProps: LEDGER_DIALOG_PAPER_PROPS,
                sx: LEDGER_DIALOG_SX,
            },
            Component: (
                <RecordPaymentDialog
                    patientStatement={patientStatement}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    claimStore={claimStore}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                />
            ),
        });
    }, [dialogContext, patientStatement, claimStore, toastContext, reloadStatement]);

    const handleRecordDiscount = useCallback((): void => {
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            await reloadStatement();
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                PaperProps: LEDGER_DIALOG_PAPER_PROPS,
                sx: LEDGER_DIALOG_SX,
            },
            Component: (
                <RecordDiscountDialog
                    patientStatement={patientStatement}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                    equalsFilter={equalsFilter}
                />
            ),
        });
    }, [dialogContext, patientStatement, toastContext, equalsFilter, reloadStatement]);

    const handleRecordRefund = useCallback((): void => {
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            await reloadStatement();
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                PaperProps: LEDGER_DIALOG_PAPER_PROPS,
                sx: LEDGER_DIALOG_SX,
            },
            Component: (
                <RecordRefundDialog
                    patientStatement={patientStatement}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    claimStore={claimStore}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                />
            ),
        });
    }, [dialogContext, patientStatement, claimStore, toastContext, reloadStatement]);

    const handleCopyClaimUrl = useCallback(async (): Promise<void> => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            toastContext.notify?.('URL copied to clipboard', 'left');
        } catch (err) {
            console.error('Failed to copy URL:', err);
            toastContext.notify?.('Failed to copy URL to clipboard', 'left', 'warning');
        }
    }, [toastContext]);

    const handlePrintPDF = useCallback((): void => {
        if (!patientStatement) {
            toastContext.notify?.('Patient statement is not loaded', 'left', 'warning');
            return;
        }

        const model = buildStatementPrintViewModel({
            patientStatement,
            ledgerRows: filteredStatementData,
            balanceBreakdown,
            amountDueOverride: filteredBalance,
            codeKeyItems: statementCodeKeyItems,
            providerClaimsById,
        });
        setPrintPreviewModel(model);
    }, [
        patientStatement,
        toastContext,
        filteredStatementData,
        balanceBreakdown,
        filteredBalance,
        statementCodeKeyItems,
        providerClaimsById,
        setPrintPreviewModel,
    ]);

    return {
        reloadStatement,
        handleEditPayment,
        handleAllocatePayment,
        handleRecordPayment,
        handleRecordDiscount,
        handleRecordRefund,
        handleCopyClaimUrl,
        handlePrintPDF,
    };
};
